/* eslint react/self-closing-comp: "off" */
/* eslint no-lonely-if: "off" */
import React, { useEffect, useState } from 'react';
import { Experiment, When } from '@carvana/core-experiment';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { getUserState } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { getSalesEffectiveDate } from '@store/sar-store';
import { BannerDisplayMode, CUSTOMER_REGISTRATION_EXPERIMENT_NAME } from '../../types';
import VehicleDetails from '../vehicle-details/VehicleDetails';
import { OwnedVehicleName, MFEShellWrapper } from './OwnedVehiclePage.styles';
import ComingSoonModal from '../_common/coming-soon-modal/ComingSoonModal';
import OVPCard from '../ovp-card-selector/OVPCard';
import FeedbackCard from '../feedback-card/FeedbackCard';
import { OvpSpinner } from '../_common/spinner/Spinner';
import { Buckets, OvpFeedTypes, ReferenceTypes } from '../../constants';

const OwnedVehiclePageContent = ({ ovpData, referenceType, ovpFeed, oneYearPurchase }: any) => {
  const { vehicle, saleDate, appraisedDate, title } = ovpData;
  const date = saleDate || appraisedDate;

  const [isLoading, setIsLoading] = useState(true);
  const [openComingSoonModal, setOpenComingSoonModal] = useState(false);
  const [filteredOvpFeed, setFilteredOvpFeed] = useState([]);
  const [feedbackCardPosition, setFeedbackCardPosition] = useState(1);

  const scrollToRegistrationCard = () => {
    const element = document.getElementById('registration-card');
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const offset = window.pageYOffset || document.documentElement.scrollTop;
    const top = rect.top + offset - 20;

    window.scrollTo({
      top,
      behavior: 'smooth',
    });

    setTimeout(() => {
      element.focus();

      if (window.location.hash !== '#registration-card') {
        window.history.replaceState(null, '', '#registration-card');
      }
    }, 600);
  };

  const isOwnedVehicle = referenceType === ReferenceTypes.VEHICLE;
  const isAppraisedVehicle = referenceType === ReferenceTypes.APPRAISAL;

  const filterOwnedVehicleExperiments = async () => {
    const experimentFilterList: string[] = [];
    const expValue: any = '';

    // STC Promo
    if (!isOwnedVehicle || !oneYearPurchase) {
      experimentFilterList.push('TradePromo');
    }

    if (expValue === Buckets.TREATMENT) {
      experimentFilterList.push(OvpFeedTypes.EXPIRED_TRADE);
    }

    return experimentFilterList;
  };

  const filterAppraisedVehicleExperiments = async () => {
    const experimentFilterList: any = [];
    const expValue: any = '';

    if (expValue === Buckets.TREATMENT) {
      experimentFilterList.push(OvpFeedTypes.EXPIRED_TRADE);
    }

    return experimentFilterList;
  };

  useEffect(() => {
    const filterFeedCards = async () => {
      let filterList: any = [];
      if (isOwnedVehicle) {
        filterList = await filterOwnedVehicleExperiments();
      } else if (isAppraisedVehicle) {
        filterList = await filterAppraisedVehicleExperiments();
      }
      const filteredData = ovpFeed.filter((ovpItem: any) => !filterList.some((type: string) => type === ovpItem.type));
      setFilteredOvpFeed(filteredData);
      setFeedbackCardPosition(filteredData.length + 1);
      setIsLoading(false);
    };

    filterFeedCards();
  }, [ovpFeed]);

  useEffect(() => {
    if (!isOwnedVehicle) return;

    const sarCard = ovpFeed?.find((item: any) => item?.type === OvpFeedTypes.SERVICE_AND_REPAIR);
    const showServiceAndRepairCard = !!sarCard;
    const isNewVehicle = sarCard?.content?.serviceAndRepair?.vehicleType === 'New';
    const saleEffectiveDate = sarCard?.content?.serviceAndRepair?.saleEffectiveDate;
    let daysSinceSaleBucket = '';
    if (saleEffectiveDate) {
      const saleDate = new Date(saleEffectiveDate);
      if (getSalesEffectiveDate(saleDate, 7)) {
        daysSinceSaleBucket = 'within 7 days';
      } else if (getSalesEffectiveDate(saleDate, 100)) {
        daysSinceSaleBucket = '7-100 days';
      } else {
        daysSinceSaleBucket = '100+ days';
      }
    }
    const trackingPayload = {
      vehicleId: vehicle.id,
      showServiceAndRepairCard,
      isNewVehicle,
      daysSinceSaleBucket,
      saleEffectiveDate,
    };
    Analytics.trackEvent('ovp - service and repair - missing from feed', trackingPayload);
    datadogRum.addAction('ovp - service and repair - missing from feed', trackingPayload);
  }, [ovpFeed]);

  useEffect(() => {
    if (window.location.hash === '#registration-card' && !isLoading) {
      setTimeout(() => {
        const element = document.getElementById('registration-card');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          element.focus();
        }
      }, 500);
    }
  }, [isLoading]);

  const userState = {
    ...ovpData,
    ...{ hostEnvironment: process.env.NODE_ENV },
  };

  if (isLoading) {
    return <OvpSpinner />;
  }

  const userId = getUserState()?.user?.user_id;

  return (
    <>
      {vehicle && (
        <>
          <OwnedVehicleName>{title}</OwnedVehicleName>
          <div style={{ marginBottom: '8px' }}>
            <Experiment name={CUSTOMER_REGISTRATION_EXPERIMENT_NAME} identifier={userId}>
              <When bucket="treatment">
                <MfeReactComponent
                  env={process.env.NODE_ENV}
                  module="CustomerRegistrationBannerMFE"
                  component="default"
                  vehicleId={vehicle.id}
                  onViewTasksClick={scrollToRegistrationCard}
                  mode={BannerDisplayMode.INTERACTIVE}
                />
              </When>
              <When default>
                <></>
              </When>
            </Experiment>
          </div>
          <VehicleDetails vehicle={vehicle} date={date} referenceType={referenceType} />
        </>
      )}
      {/* MFEs of different cards are loaded here */}
      <MFEShellWrapper>
        {/* OVP Cards returned by Feed API */}
        {filteredOvpFeed.map((feedCard: any, index: any) => (
          <React.Fragment key={`${feedCard.type}${index}`}>
            <OVPCard feedCard={feedCard} positionValue={index + 1} userState={userState} vehicleId={vehicle.id} />
          </React.Fragment>
        ))}
        {/* Feedback Card */}
        <FeedbackCard key={feedbackCardPosition} cardPosition={feedbackCardPosition} />
      </MFEShellWrapper>

      <ComingSoonModal isOpen={openComingSoonModal} closeModal={() => setOpenComingSoonModal(false)} />
    </>
  );
};

export default OwnedVehiclePageContent;
